/**
 * The shell's strings, and the one knob a game turns: which locale.
 *
 * The shell does not decide the language, persist it or read it from the
 * browser. The game owns that (TwinTen: `apps/client/src/i18n/locale.svelte.ts`)
 * and calls {@link setShellLocale} whenever its own locale changes — which is
 * what keeps "the game's language" one fact rather than two that can drift.
 *
 * A `$state` rune, so a shell component that calls {@link shellText} from its
 * template re-renders on a switch with nothing more to wire up.
 */
import { shellEn, type ShellStringKey } from './i18n/en.js';
import { shellNl } from './i18n/nl.js';

export type ShellLocale = 'en' | 'nl';
export type { ShellStringKey } from './i18n/en.js';

const catalogs: Record<ShellLocale, Record<ShellStringKey, string>> = {
  en: shellEn,
  nl: shellNl,
};

let locale = $state<ShellLocale>('en');

/** Called by the game whenever its own locale changes. Anything unknown falls back to English. */
export function setShellLocale(next: string): void {
  locale = next === 'nl' ? 'nl' : 'en';
}

export function getShellLocale(): ShellLocale {
  return locale;
}

/**
 * The shell's string for `key` in the current locale, with any `{name}`
 * tokens filled from `vars` — the same shape a game's own `t()` has.
 */
export function shellText(
  key: ShellStringKey,
  vars?: Readonly<Record<string, string | number>>,
): string {
  let text: string = catalogs[locale][key];
  if (vars === undefined) return text;
  for (const [name, value] of Object.entries(vars)) {
    text = text.replaceAll(`{${name}}`, String(value));
  }
  return text;
}

/** Test-only: every catalogue, so a test can assert the two never drift. */
export const shellCatalogs: Readonly<
  Record<ShellLocale, Readonly<Record<ShellStringKey, string>>>
> = catalogs;
